import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { fetchDashboardVideos, fetchVideoNudgePreview, sendVideoNudge, isAdminUser } from "@/lib/api";
import { Loader2, Clapperboard, Mail, MailOpen, ChevronDown, ArrowLeft, Search, Users, Send, Megaphone } from "lucide-react";
import { toast } from "sonner";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const whenTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function VideosReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const recId = user?.user_record_id || user?.record_id;
  const admin = isAdminUser(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState("");
  const [byDealer, setByDealer] = useState(true);
  const [openGroup, setOpenGroup] = useState("");

  useEffect(() => {
    if (!recId) return;
    setLoading(true);
    fetchDashboardVideos(recId).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [recId]);

  const videos = useMemo(() => {
    const all = data?.videos || [];
    const t = q.trim().toLowerCase();
    return !t ? all : all.filter((v) => [v.template_name, v.business_name, v.id].some((x) => String(x || "").toLowerCase().includes(t)));
  }, [data, q]);
  const opened = (data?.videos || []).reduce((a, v) => a + (v.emails_opened || 0), 0);
  const groups = useMemo(() => {
    const m = new Map();
    for (const v of videos) {
      const key = v.dealer_rec || v.business_name || "—";
      if (!m.has(key)) m.set(key, { key, name: v.business_name || "Unassigned", videos: [] });
      m.get(key).videos.push(v);
    }
    return [...m.values()].sort((a, b) => b.videos.length - a.videos.length || a.name.localeCompare(b.name));
  }, [videos]);

  return (
    <div className="pb-24" data-testid="videos-report-page">
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={() => navigate(-1)} className="h-8 w-8 rounded-full border border-zinc-200 bg-white inline-flex items-center justify-center text-zinc-600 shrink-0" aria-label="Back" data-testid="videos-report-back">
          <ArrowLeft size={15} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-zinc-900 flex items-center gap-1.5 leading-tight"><Clapperboard size={16} className="text-violet-600" /> Videos created</h1>
          <p className="text-[11px] text-zinc-500 truncate" data-testid="videos-report-summary">
            {data ? `${data.created} video${data.created === 1 ? "" : "s"} · ${data.emails_sent} email${data.emails_sent === 1 ? "" : "s"} sent · ${opened} opened` : "Every video made and the emails it went out in."}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search video, business, id…" className="w-full h-9 rounded-xl border border-zinc-200 bg-white pl-8 pr-3 text-sm" data-testid="videos-report-search" />
        </div>
        <div className="flex rounded-full border border-zinc-200 bg-white p-0.5 text-[11px] font-semibold shrink-0" data-testid="videos-report-view">
          {[[true, "By dealer"], [false, "By date"]].map(([v, l]) => (
            <button key={l} type="button" onClick={() => setByDealer(v)} className={`px-2.5 h-7 rounded-full ${byDealer === v ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid={`videos-view-${v ? "dealer" : "date"}`}>{l}</button>
          ))}
        </div>
      </div>
      {admin && <NudgePanel recId={recId} />}
      {byDealer && data && !loading && videos.length > 0 && (
        <div className="text-[11px] text-zinc-500 mb-1.5" data-testid="videos-report-dealer-count">{groups.length} dealer{groups.length === 1 ? "" : "s"} · sorted by videos made</div>
      )}

      {loading ? (
        <div className="py-12 flex justify-center text-zinc-400"><Loader2 className="animate-spin" size={22} /></div>
      ) : !videos.length ? (
        <div className="py-12 text-center text-sm text-zinc-500 rounded-2xl border border-dashed border-zinc-200 bg-white">No videos yet — make one in Marketing AI › Media.</div>
      ) : byDealer ? (
        <div className="space-y-2" data-testid="videos-report-groups">
          {groups.map((g, i) => {
            const isOpen = openGroup ? openGroup === g.key : i === 0;
            const sent = g.videos.reduce((a, v) => a + v.emails_sent, 0);
            const op = g.videos.reduce((a, v) => a + v.emails_opened, 0);
            return (
              <div key={g.key} className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid={`dealer-group-${g.key}`}>
                <button type="button" onClick={() => setOpenGroup(isOpen ? "__none" : g.key)} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left bg-zinc-50/70" data-testid={`dealer-group-toggle-${g.key}`}>
                  <div className="h-8 w-8 rounded-full bg-violet-600 text-white text-sm font-bold flex items-center justify-center shrink-0" data-testid={`dealer-group-count-${g.key}`}>{g.videos.length}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-zinc-900 truncate">{g.name}</div>
                    <div className="text-[11px] text-zinc-500">{g.videos.length} video{g.videos.length === 1 ? "" : "s"} · {sent} sent · {op} opened</div>
                  </div>
                  <ChevronDown size={14} className={`text-zinc-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="divide-y divide-zinc-100 border-t border-zinc-100">
                    {g.videos.map((v) => <VideoRow key={v.id} v={v} open={openId === v.id} onToggle={() => setOpenId(openId === v.id ? "" : v.id)} canSend={admin || v.dealer_rec === recId} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100 overflow-hidden" data-testid="videos-report-list">
          {videos.map((v) => <VideoRow key={v.id} v={v} open={openId === v.id} onToggle={() => setOpenId(openId === v.id ? "" : v.id)} canSend={admin || v.dealer_rec === recId} />)}
        </div>
      )}
    </div>
  );
}

function NudgePanel({ recId }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!recId) return;
    fetchVideoNudgePreview(recId).then(setInfo).catch(() => setInfo(null));
  }, [recId]);
  if (!info) return null;
  const pending = info.dealers.filter((d) => !d.nudged_at);
  async function send() {
    setSending(true);
    try {
      const r = await sendVideoNudge(recId, pending.map((d) => d.id));
      toast.success(`Emailing ${r.queued} dealer${r.queued === 1 ? "" : "s"} — opens will show in CRM`);
      setConfirm(false);
      setInfo(await fetchVideoNudgePreview(recId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  }
  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/60 mb-2" data-testid="video-nudge-panel">
      <div className="flex items-center gap-2 p-2.5">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 flex items-center gap-2 text-left" data-testid="video-nudge-toggle">
          <Megaphone size={15} className="text-violet-700 shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-zinc-900" data-testid="video-nudge-count">{info.count} dealer{info.count === 1 ? "" : "s"} haven't made a video</div>
            <div className="text-[11px] text-zinc-500">{pending.length} not yet nudged · {info.dealers.filter((d) => d.opened).length} opened the nudge</div>
          </div>
          <ChevronDown size={14} className={`text-violet-700 shrink-0 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {!confirm ? (
          <button type="button" disabled={!pending.length} onClick={() => setConfirm(true)} className="h-8 px-2.5 rounded-lg bg-violet-600 text-white text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-40 shrink-0" data-testid="video-nudge-send">
            <Send size={12} /> Nudge {pending.length}
          </button>
        ) : (
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" disabled={sending} onClick={send} className="h-8 px-2.5 rounded-lg bg-violet-700 text-white text-[11px] font-semibold" data-testid="video-nudge-confirm">{sending ? <Loader2 size={12} className="animate-spin" /> : `Email ${pending.length}`}</button>
            <button type="button" onClick={() => setConfirm(false)} className="h-8 px-2 rounded-lg text-[11px] font-semibold text-zinc-600" data-testid="video-nudge-cancel">Cancel</button>
          </div>
        )}
      </div>
      {open && (
        <ul className="border-t border-violet-100 max-h-56 overflow-y-auto divide-y divide-violet-100 text-xs" data-testid="video-nudge-list">
          {info.dealers.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
              <div className="min-w-0"><div className="font-medium text-zinc-800 truncate">{d.company || d.name}</div><div className="text-[10px] text-zinc-400 truncate">{d.email}</div></div>
              <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${d.opened ? "bg-emerald-50 text-emerald-700" : d.nudged_at ? "bg-zinc-100 text-zinc-600" : "bg-amber-50 text-amber-700"}`}>{d.opened ? "Opened" : d.nudged_at ? "Nudged" : "Not nudged"}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VideoRow({ v, open, onToggle, canSend }) {
  const navigate = useNavigate();
  return (
    <div data-testid={`video-row-${v.id}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-zinc-50" data-testid={`video-row-toggle-${v.id}`}>
        <div className="w-7 h-12 rounded-md bg-zinc-900 overflow-hidden shrink-0">
          {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-zinc-900 truncate">{v.template_name}</div>
          <div className="text-[11px] text-zinc-500 truncate">{v.business_name}{v.business_name ? " · " : ""}{when(v.created_at)}</div>
        </div>
        <div className="text-right shrink-0 text-[11px] leading-tight flex flex-col items-end gap-0.5" data-testid={`video-emails-${v.id}`}>
          <div className="inline-flex items-center gap-1 text-zinc-700 font-semibold"><Mail size={11} /> {v.emails_sent} sent</div>
          <div className={`inline-flex items-center gap-1 ${v.emails_opened ? "text-emerald-700" : "text-zinc-400"}`}><MailOpen size={11} /> {v.emails_opened} opened</div>
        </div>
        <ChevronDown size={14} className={`text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-zinc-100 bg-zinc-50/60 p-3 space-y-3 sm:grid sm:grid-cols-[220px_1fr] sm:gap-4 sm:space-y-0" data-testid={`video-detail-${v.id}`}>
          <div>
            {v.video_url && <video src={v.video_url} poster={v.thumbnail_url} controls playsInline className="w-full max-h-64 sm:max-h-[380px] rounded-xl bg-black" data-testid={`video-player-${v.id}`} />}
            <div className="mt-1.5 text-[11px] text-zinc-500 break-all">ID {v.id}{v.video_url ? <> · <a href={v.video_url} target="_blank" rel="noreferrer" className="text-violet-700 underline">video link</a></> : null}</div>
            {canSend && v.video_url && (
              <button type="button" onClick={() => navigate(`/app/contacts?video=${encodeURIComponent(v.id)}`)} className="mt-2 h-8 px-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs font-semibold inline-flex items-center gap-1" data-testid={`video-send-crm-${v.id}`}>
                <Users size={12} /> Send to CRM
              </button>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-1">Emails with this video</div>
            {!v.emails.length ? (
              <div className="text-xs text-zinc-500">Not emailed yet — use “Send to CRM” on the video.</div>
            ) : (
              <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white">
                {v.emails.map((e, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs" data-testid={`video-email-${v.id}-${i}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-800 truncate">{e.to_email || "(unknown)"}</div>
                      <div className="text-[10px] text-zinc-400">sent {whenTime(e.sent_at)}</div>
                    </div>
                    <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${e.opened ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{e.opened ? "Opened" : "Sent"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
